import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { Home, FileText, TrendingUp, Users, UserCircle, BarChart3, Menu, LogOut, Sun, Moon, MessageCircle, Shield, Warehouse, Snowflake, Package } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { toast } from 'sonner';

const Layout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [open, setOpen] = useState(false);

  const handleLogout = () => {
    logout();
    toast.success('Sesión cerrada');
    navigate('/login');
  };

  const allNavigation = [
    { name: 'Dashboard', href: '/', icon: Home },
    { name: 'Ofertas', href: '/publicaciones', icon: FileText },
    { name: 'Bids', href: '/ofertas', icon: TrendingUp },
    { name: 'Mensajes', href: '/mensajes', icon: MessageCircle, adminOnly: true },
    { name: 'Compradores', href: '/compradores', icon: Users, adminOnly: true },
    { name: 'Frigoríficos', href: '/frigorificos', icon: Snowflake, adminOnly: true },
    { name: 'Productos', href: '/productos', icon: Package, adminOnly: true },
    { name: 'Usuarios', href: '/usuarios', icon: UserCircle, adminOnly: true },
    { name: 'Reportes', href: '/reportes', icon: BarChart3, adminOnly: true },
    { name: 'Auditoría', href: '/auditoria', icon: Shield, adminOnly: true },
  ];

  const navigation = allNavigation.filter(
    item => {
      if (item.superAdminOnly) return user?.rol === 'SuperAdmin';
      if (item.adminOnly) return user?.rol === 'Admin' || user?.rol === 'SuperAdmin';
      return true;
    }
  );

  const NavItems = ({ mobile = false }) => (
    <nav className="flex flex-col gap-1">
      {navigation.map((item) => {
        const Icon = item.icon;
        const isActive = location.pathname === item.href ||
          (item.href !== '/' && location.pathname.startsWith(item.href));
        return (
          <Link
            key={item.name}
            to={item.href}
            onClick={() => mobile && setOpen(false)}
            className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200 ${isActive
              ? 'bg-primary/20 text-primary border border-primary/30'
              : 'text-muted-foreground hover:bg-accent hover:text-foreground border border-transparent'
              }`}
            data-testid={`nav-${item.name.toLowerCase()}`}
          >
            <Icon className="h-5 w-5" />
            {item.name}
          </Link>
        );
      })}
    </nav>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {/* Sidebar Desktop */}
      <aside className="hidden lg:flex lg:flex-col lg:w-64 bg-card border-r border-border">
        <div className="flex h-16 items-center border-b border-border px-6">
          <Link to="/" className="flex items-center gap-2 w-full justify-center">
            <Snowflake className="h-7 w-7 text-primary" />
            <span className="text-lg font-bold tracking-tight text-foreground">FrigoPost+</span>
          </Link>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          <NavItems />
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden bg-background">
        {/* Top Bar */}
        <header className="sticky top-0 z-40 border-b border-border bg-card/50 backdrop-blur supports-[backdrop-filter]:bg-card/50">
          <div className="flex h-16 items-center justify-between px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-4">
              {/* Mobile Menu */}
              <Sheet open={open} onOpenChange={setOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-button">
                    <Menu className="h-6 w-6" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-64 p-0 bg-card border-border flex flex-col h-full">
                  <div className="flex h-16 items-center border-b border-slate-800/60 px-6 shrink-0">
                    <Link to="/" className="flex items-center gap-2 w-full justify-center" onClick={() => setOpen(false)}>
                      <Snowflake className="h-7 w-7 text-primary" />
                      <span className="text-lg font-bold tracking-tight text-foreground">FrigoPost+</span>
                    </Link>
                  </div>
                  <div className="p-4 flex-1 overflow-y-auto">
                    <NavItems mobile />
                  </div>
                </SheetContent>
              </Sheet>

              <Link to="/" className="lg:hidden flex items-center gap-2">
                <Snowflake className="h-6 w-6 text-primary" />
                <span className="text-base font-bold text-foreground">FrigoPost+</span>
              </Link>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground hidden sm:inline">
                {user?.nombre} <span className="text-muted-foreground/70">({user?.rol})</span>
              </span>
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="h-9 w-9"
                title={theme === 'dark' ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
                data-testid="theme-toggle-button"
              >
                {theme === 'dark' ? (
                  <Sun className="h-4 w-4" />
                ) : (
                  <Moon className="h-4 w-4" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="gap-2"
                data-testid="logout-button"
              >
                <LogOut className="h-4 w-4" />
                <span className="hidden sm:inline">Salir</span>
              </Button>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto bg-background">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;